import { useState, useRef, useEffect } from "react";
import type maplibregl from "maplibre-gl";
import {
  Search,
  MapPin,
  Trash2,
  Plus,
  Pencil,
  Eye,
  EyeOff,
  Ellipsis,
  Info,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Item,
  ItemGroup,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemActions,
} from "@/components/ui/item";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

import {
  LAYER_DISPLAY_CONFIG,
  PMTILES_SOURCE_LAYERS,
} from "../config/deforestation";
import type {
  DeforestationLayerStates,
  Group,
  ImageryPicks,
  ParcelInfo,
  SearchMode,
} from "../types/deforestation";

interface SearchHit {
  cadastrePid: string;
  parcelId: string;
  lot: string | null;
  plan: string | null;
  state: string;
}

function searchVisibleCadastre(
  m: maplibregl.Map | null,
  query: string,
  mode: SearchMode,
): SearchHit[] {
  if (!m || query.trim().length === 0) return [];
  const features = m.queryRenderedFeatures({
    layers: ["geoscape-cadastre-fill"],
  });
  const q = query.toLowerCase();
  const seen = new Set<string>();
  const hits: SearchHit[] = [];
  for (const f of features) {
    const props = (f.properties ?? {}) as Record<string, unknown>;
    const cadastrePid =
      typeof props.cadastrePid === "string" ? props.cadastrePid : null;
    if (!cadastrePid || seen.has(cadastrePid)) continue;
    const parcelId =
      typeof props.parcelId === "string" ? props.parcelId : cadastrePid;
    const lot = typeof props.lot === "string" ? props.lot : null;
    const plan = typeof props.plan === "string" ? props.plan : null;
    const state = typeof props.state === "string" ? props.state : "";

    let match = false;
    if (mode === "parcel") {
      match =
        parcelId.toLowerCase().includes(q) ||
        cadastrePid.toLowerCase().includes(q) ||
        (lot ? lot.toLowerCase().includes(q) : false) ||
        (plan ? plan.toLowerCase().includes(q) : false);
    } else {
      match =
        parcelId.toLowerCase().includes(q) ||
        state.toLowerCase().includes(q);
    }
    if (match) {
      seen.add(cadastrePid);
      hits.push({ cadastrePid, parcelId, lot, plan, state });
      if (hits.length >= 25) break;
    }
  }
  return hits;
}

interface LeftPanelProps {
  searchMode: SearchMode;
  searchQuery: string;
  onSearchModeChange: (mode: SearchMode) => void;
  onSearchQueryChange: (query: string) => void;
  mapRef: React.RefObject<maplibregl.Map | null>;
  groups: Group[];
  activeGroupId: string | null;
  focusedParcel: string | null;
  parcelData: Map<string, ParcelInfo>;
  onSelectGroup: (groupId: string) => void;
  onAddGroup: () => void;
  onDeleteGroup: (groupId: string) => void;
  onRenameGroup: (groupId: string, name: string) => void;
  onRemoveParcel: (groupId: string, cadastrePid: string) => void;
  onAddParcelToGroup: (cadastrePid: string) => void;
  onFocusParcel: (cadastrePid: string) => void;
  layers: DeforestationLayerStates;
  onToggleLayer: (layerKey: keyof DeforestationLayerStates) => void;
  onSetLayerOpacity: (
    layerKey: keyof DeforestationLayerStates,
    opacity: number,
  ) => void;
  onSetLayerColor: (
    layerKey: keyof DeforestationLayerStates,
    color: string,
  ) => void;
  imageryPicks: Partial<Record<"2020" | "2025", ImageryPicks>>;
}

// Maps imagery layer keys to the year that picks.json keys off. Keeps
// the popover dumb — it gets a `picks` prop or undefined and renders
// the table accordingly.
const IMAGERY_PICKS_KEY: Partial<
  Record<keyof DeforestationLayerStates, "2020" | "2025">
> = {
  imagery2020: "2020",
  imagery2025: "2025",
};

function ImageryPicksTable({
  layerKey,
  imageryPicks,
}: {
  layerKey: keyof DeforestationLayerStates;
  imageryPicks: Partial<Record<"2020" | "2025", ImageryPicks>>;
}) {
  const year = IMAGERY_PICKS_KEY[layerKey];
  const picks = year ? imageryPicks[year] : undefined;
  if (!picks) return null;
  return (
    <div className="border-t pt-2">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        Capture dates ({picks.date_range[0]} → {picks.date_range[1]})
      </p>
      <ul className="mt-1 space-y-0.5">
        {picks.scenes.map((s) => (
          <li
            key={s.item_id}
            className="flex items-center justify-between gap-2 font-mono text-[10px] text-muted-foreground"
          >
            <span>{s.mgrs_tile ?? "?"}</span>
            <span>{s.datetime}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface EditableNameProps {
  name: string;
  onSave: (name: string) => void;
}

function EditableName({ name, onSave }: EditableNameProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  useEffect(() => {
    setValue(name);
  }, [name]);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed) onSave(trimmed);
    else setValue(name);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        aria-label={`Rename group ${name}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setValue(name);
            setEditing(false);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className="h-5 min-w-0 flex-1 rounded border border-primary bg-transparent px-1 text-xs font-semibold outline-none"
      />
    );
  }

  return (
    <>
      <span className="flex-1 truncate text-xs font-semibold">{name}</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        title="Rename group"
      >
        <Pencil className="h-3 w-3" />
      </Button>
    </>
  );
}

export function LeftPanel({
  searchMode,
  searchQuery,
  onSearchModeChange,
  onSearchQueryChange,
  mapRef,
  groups,
  activeGroupId,
  focusedParcel,
  parcelData,
  onSelectGroup,
  onAddGroup,
  onDeleteGroup,
  onRenameGroup,
  onRemoveParcel,
  onAddParcelToGroup,
  onFocusParcel,
  layers,
  onToggleLayer,
  onSetLayerOpacity,
  onSetLayerColor,
  imageryPicks,
}: LeftPanelProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchHits, setSearchHits] = useState<SearchHit[]>([]);

  // Querying rendered features is expensive, so only touch the map when the
  // search inputs actually change — not on every layer-state re-render (e.g.
  // opacity-slider drags). An empty query short-circuits to no hits.
  useEffect(() => {
    if (searchQuery.trim().length === 0) {
      setSearchHits([]);
      return;
    }
    setSearchHits(
      searchVisibleCadastre(mapRef.current, searchQuery, searchMode),
    );
  }, [searchQuery, searchMode, mapRef]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Search */}
      <div className="border-b p-3">
        <div className="mb-2 flex gap-1">
          <Button
            variant={searchMode === "address" ? "default" : "secondary"}
            size="sm"
            className="flex-1 text-xs"
            onClick={() => onSearchModeChange("address")}
          >
            <MapPin className="mr-1 h-3 w-3" />
            Address
          </Button>
          <Button
            variant={searchMode === "parcel" ? "default" : "secondary"}
            size="sm"
            className="flex-1 text-xs"
            onClick={() => onSearchModeChange("parcel")}
          >
            <Search className="mr-1 h-3 w-3" />
            Parcel ID
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={
              searchMode === "address"
                ? "Search by address..."
                : "Search by parcel ID..."
            }
            value={searchQuery}
            onChange={(e) => {
              onSearchQueryChange(e.target.value);
              setDropdownOpen(true);
            }}
            onFocus={() => setDropdownOpen(true)}
            // Delay so a result button's onClick fires before the dropdown
            // unmounts; clicking outside the input then dismisses it.
            onBlur={() => window.setTimeout(() => setDropdownOpen(false), 150)}
            className="pl-9 text-sm"
          />
          {searchQuery.trim().length > 0 &&
            searchHits.length > 0 &&
            dropdownOpen && (
              <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
                {searchHits.map((hit) => (
                  <button
                    key={hit.cadastrePid}
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                    onClick={() => {
                      onAddParcelToGroup(hit.cadastrePid);
                      setDropdownOpen(false);
                    }}
                  >
                    <span className="truncate font-mono text-xs">
                      {hit.parcelId}
                    </span>
                    {hit.state && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {hit.state}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          {searchQuery.trim().length > 0 && searchHits.length === 0 && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              No matches in current view — pan/zoom to load more parcels.
            </p>
          )}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Searching uses parcels currently rendered on the map. Cadastre layer
          is keyed by{" "}
          <span className="font-mono">
            {PMTILES_SOURCE_LAYERS.cadastre}
          </span>
          .
        </p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      {/* Groups */}
      <div className="flex flex-col border-b">
        <div className="flex items-center justify-between px-3 pt-3 pb-2">
          <span className="text-sm font-semibold">Reporting</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={onAddGroup}
            title="Add group"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
        {groups.length === 0 ? (
          <p className="px-3 pb-3 text-xs text-muted-foreground">
            Click parcels on map or search to add
          </p>
        ) : (
          <div className="px-1">
            <div className="space-y-2 px-2 pb-2">
              {groups.map((group) => (
                <div key={group.id}>
                  <div
                    className={cn(
                      "flex items-center gap-1.5 rounded-md px-2 py-1 cursor-pointer",
                      activeGroupId === group.id &&
                        "bg-accent text-accent-foreground",
                    )}
                    onClick={() => onSelectGroup(group.id)}
                  >
                    <EditableName
                      name={group.name}
                      onSave={(name) => onRenameGroup(group.id, name)}
                    />
                    <Badge
                      variant="outline"
                      className={cn(
                        "shrink-0",
                        activeGroupId === group.id &&
                          "text-accent-foreground",
                      )}
                    >
                      {group.parcelIds.length}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteGroup(group.id);
                      }}
                      title="Delete group"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {group.parcelIds.length > 0 && (
                    <div className="ml-3">
                      <ItemGroup className="my-1 gap-1">
                        {group.parcelIds.map((cadastrePid) => {
                          const parcel = parcelData.get(cadastrePid);
                          const label = parcel?.parcel_id ?? cadastrePid;
                          return (
                            <Item
                              key={cadastrePid}
                              size="xs"
                              className={cn(
                                "py-0.5 cursor-pointer",
                                focusedParcel === cadastrePid
                                  ? "bg-accent text-accent-foreground hover:bg-accent/80 hover:text-accent-foreground"
                                  : "hover:bg-muted",
                              )}
                              onClick={() => onFocusParcel(cadastrePid)}
                            >
                              <ItemMedia variant="icon">
                                <span className="h-3 w-3 rounded-full bg-muted-foreground/40" />
                              </ItemMedia>
                              <ItemContent>
                                <ItemTitle className="truncate font-mono text-xs">
                                  {label}
                                </ItemTitle>
                              </ItemContent>
                              <ItemActions>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "h-6 w-6 text-muted-foreground hover:text-destructive",
                                    focusedParcel === cadastrePid &&
                                      "text-muted hover:text-destructive",
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveParcel(group.id, cadastrePid);
                                  }}
                                  title="Remove parcel"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </ItemActions>
                            </Item>
                          );
                        })}
                      </ItemGroup>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Layers */}
      <div className="p-3">
        <h3 className="mb-2 text-sm font-semibold">Layers</h3>
        <div className="space-y-3">
          {LAYER_DISPLAY_CONFIG.map(({ group, layers: groupLayers }) => (
            <div key={group}>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group}
              </p>
              <div className="space-y-0.5">
                {groupLayers.map((layer) => {
                  const unavailable = layer.available === false;
                  return (
                    <div
                      key={layer.key}
                      className="flex items-center gap-1 rounded pl-3 py-0.5 text-sm"
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "h-3 w-3 shrink-0 rounded-full border border-border",
                          unavailable && "opacity-30",
                        )}
                        style={{ backgroundColor: layers[layer.key].color }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (!unavailable) onToggleLayer(layer.key);
                        }}
                        disabled={unavailable}
                        className={cn(
                          "flex-1 text-left text-xs",
                          unavailable
                            ? "text-red-500 cursor-not-allowed"
                            : cn(
                                "cursor-pointer hover:text-foreground",
                                !layers[layer.key].visible &&
                                  "text-muted-foreground",
                              ),
                        )}
                        title={
                          unavailable
                            ? "Data not yet available — see scripts/demo-nab-deforestation/LAYERS_STATUS.md"
                            : layers[layer.key].visible
                              ? "Hide layer"
                              : "Show layer"
                        }
                      >
                        {layer.label}
                      </button>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            title="Layer info"
                          >
                            <Info className="h-3.5 w-3.5" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="right"
                          align="start"
                          className="w-64 p-3"
                        >
                          <div className="space-y-2">
                            <p className="text-xs font-medium">{layer.label}</p>
                            {unavailable && (
                              <p className="text-[11px] font-medium text-red-500">
                                Data not yet available — toggle disabled.
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {layer.description}
                            </p>
                            <ImageryPicksTable
                              layerKey={layer.key}
                              imageryPicks={imageryPicks}
                            />
                            <div className="border-t pt-2">
                              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                Attribution
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {layer.attribution}
                              </p>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            title="Layer options"
                            disabled={unavailable}
                          >
                            <Ellipsis className="h-3.5 w-3.5" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="right"
                          align="start"
                          className="w-48 p-3"
                        >
                          <div className="space-y-3">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">Opacity</span>
                                <span className="text-xs text-muted-foreground">
                                  {Math.round(layers[layer.key].opacity * 100)}%
                                </span>
                              </div>
                              <Slider
                                value={[layers[layer.key].opacity * 100]}
                                min={0}
                                max={100}
                                step={5}
                                onValueChange={([v]) =>
                                  onSetLayerOpacity(layer.key, v / 100)
                                }
                              />
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">Colour</span>
                                <span className="font-mono text-[10px] uppercase text-muted-foreground">
                                  {layers[layer.key].color}
                                </span>
                              </div>
                              <label className="flex cursor-pointer items-center gap-2">
                                <span
                                  aria-hidden
                                  className="h-6 w-6 shrink-0 rounded border border-border"
                                  style={{ backgroundColor: layers[layer.key].color }}
                                />
                                <input
                                  type="color"
                                  value={layers[layer.key].color}
                                  onChange={(e) =>
                                    onSetLayerColor(layer.key, e.target.value)
                                  }
                                  className="h-6 w-full cursor-pointer rounded border border-border bg-transparent"
                                />
                              </label>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => onToggleLayer(layer.key)}
                        title={
                          unavailable
                            ? "Layer data not available"
                            : layers[layer.key].visible
                              ? "Hide layer"
                              : "Show layer"
                        }
                        disabled={unavailable}
                      >
                        {layers[layer.key].visible && !unavailable ? (
                          <Eye className="h-3.5 w-3.5" />
                        ) : (
                          <EyeOff
                            className={cn(
                              "h-3.5 w-3.5",
                              unavailable
                                ? "text-red-500"
                                : "text-muted-foreground",
                            )}
                          />
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
